import { useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listJobs, uploadFiles, deleteJob, getJob } from '../api'
import type { JobSummary } from '../types'

const POLL_INTERVAL = 2500 // ms — poll while any job is processing/queued

export function useJobs() {
  const qc = useQueryClient()

  const { data: jobs = [], isLoading, isError, error } = useQuery({
    queryKey: ['jobs'],
    queryFn: listJobs,
    refetchInterval: (query) => {
      const jobs = query.state.data as JobSummary[] | undefined
      const hasActive = jobs?.some((j) => j.status === 'queued' || j.status === 'processing')
      return hasActive ? POLL_INTERVAL : false
    },
  })

 const upload = useMutation({
  mutationFn: ({ files, rateMode }: { files: File[]; rateMode: 'reduced' | 'standard' }) =>
    uploadFiles(files, rateMode),
  onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
})

  const remove = useMutation({
    mutationFn: (id: string) => deleteJob(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['jobs'] })
  }, [qc])

  const stats = {
    total_vat:     jobs.filter(j => j.status === 'done').reduce((s, j) => s + j.total_vat_eur, 0),
    total_spend:   jobs.filter(j => j.status === 'done').reduce((s, j) => s + j.total_spend_eur, 0),
    total_txns:    jobs.filter(j => j.status === 'done').reduce((s, j) => s + j.total_transactions, 0),
    files_done:    jobs.filter(j => j.status === 'done').length,
    files_active:  jobs.filter(j => j.status === 'queued' || j.status === 'processing').length,
    files_error:   jobs.filter(j => j.status === 'error').length,
  }

  return { jobs, isLoading, isError, error, upload, remove, refresh, stats }
}

export function useJobDetail(id: string | null) {
  return useQuery({
    queryKey: ['job', id],
    queryFn: () => getJob(id!),
    enabled: !!id,
    staleTime: 5_000,
  })
}